import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { moviesApi } from "@/api/movies";
import type { GrabRecord, Movie } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { SearchInput } from "@/components/ui/Field";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Movies that are monitored but don't yet have a file in the library.
 * Drives the "Wanted" tab — equivalent of Radarr's Missing list.
 *
 * Per-row Search & Grab is now optimistic: the row immediately flips
 * to a "queued" status and fades down 50%, so the UI feels reactive
 * even on slow indexers. The actual GrabRecord (with indexer name)
 * arrives 1-3s later and replaces the optimistic state.
 */

type RowState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "grabbed"; record: GrabRecord }
  | { kind: "failed"; reason: string };

export function MoviesWanted() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [searchingAll, setSearchingAll] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [filter, setFilter] = useState("");
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});

  const { data: wanted, isLoading } = useQuery({
    queryKey: ["movies", "wanted"],
    queryFn: () => moviesApi.wanted(500),
  });

  const grabMut = useMutation({
    mutationFn: (id: number) => moviesApi.search(id),
  });

  const lower = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!wanted) return [];
    if (!lower) return wanted;
    return wanted.filter(
      (m) =>
        m.title.toLowerCase().includes(lower) ||
        String(m.year ?? "").includes(lower),
    );
  }, [wanted, lower]);

  const onGrab = (movie: Movie) => {
    setRowStates((s) => ({ ...s, [movie.id]: { kind: "searching" } }));
    grabMut.mutate(movie.id, {
      onSuccess: (rec) => {
        setRowStates((s) => ({
          ...s,
          [movie.id]: { kind: "grabbed", record: rec },
        }));
        toast(
          `Grabbed ${rec.release_title} via ${rec.indexer_name ?? "?"} → ${rec.download_client_name ?? "?"}`,
          "success",
        );
        qc.invalidateQueries({ queryKey: ["movies", "wanted"] });
        qc.invalidateQueries({ queryKey: ["grabs"] });
      },
      onError: (err) => {
        const reason = err instanceof Error ? err.message : "Search failed";
        setRowStates((s) => ({ ...s, [movie.id]: { kind: "failed", reason } }));
        toast(reason, "error");
      },
    });
  };

  const searchAll = async () => {
    if (!filtered || filtered.length === 0) return;
    if (
      !confirm(
        `Search ${filtered.length} monitored movie${filtered.length === 1 ? "" : "s"} across your indexers? This may take several minutes.`,
      )
    ) {
      return;
    }
    setSearchingAll(true);
    setProgress({ done: 0, total: filtered.length });
    let succeeded = 0;
    let failed = 0;

    const CONCURRENCY = 3;
    const queue = filtered.slice();
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length > 0) {
        const movie = queue.shift();
        if (!movie) return;
        setRowStates((s) => ({ ...s, [movie.id]: { kind: "searching" } }));
        try {
          const rec = await moviesApi.search(movie.id);
          setRowStates((s) => ({
            ...s,
            [movie.id]: { kind: "grabbed", record: rec },
          }));
          succeeded += 1;
        } catch (err) {
          const reason = err instanceof Error ? err.message : "failed";
          setRowStates((s) => ({
            ...s,
            [movie.id]: { kind: "failed", reason },
          }));
          failed += 1;
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    });
    await Promise.all(workers);
    setSearchingAll(false);
    qc.invalidateQueries({ queryKey: ["movies", "wanted"] });
    qc.invalidateQueries({ queryKey: ["movies"] });
    qc.invalidateQueries({ queryKey: ["grabs"] });
    toast(
      `Searched ${filtered.length} movies — ${succeeded} grabbed, ${failed} no match / failed`,
      succeeded > 0 ? "success" : "info",
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Wanted"
        title="Movies missing files"
        description="Monitored films without a copy in your library. Search per row, or batch-search the whole list."
        actions={
          <Button
            onClick={searchAll}
            loading={searchingAll}
            disabled={!filtered || filtered.length === 0}
          >
            {searchingAll
              ? `Searching ${progress.done}/${progress.total}`
              : "Search all"}
          </Button>
        }
      />

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title or year"
          />
        </div>
        {wanted && (
          <span className="font-mono text-xs text-foreground/55">
            {filter
              ? `${filtered.length} of ${wanted.length}`
              : `${wanted.length} wanted`}
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton.Rows count={8} />
      ) : !wanted || wanted.length === 0 ? (
        <EmptyState
          glyph="film"
          title="Nothing wanted"
          description="Every monitored movie has a file. Add more or unmonitor some."
          action={
            <Link to="/movies">
              <Button variant="secondary">Back to library</Button>
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description={`Nothing matches "${filter}".`}
          action={
            <Button variant="ghost" onClick={() => setFilter("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50">
              <tr className="text-left">
                <th className="w-16 px-3 py-2.5"></th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Title
                </th>
                <th className="px-3 py-2.5 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Year
                </th>
                <th className="px-3 py-2.5 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Runtime
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Status
                </th>
                <th className="w-32 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((m) => (
                <WantedRow
                  key={m.id}
                  movie={m}
                  state={rowStates[m.id] ?? { kind: "idle" }}
                  onGrab={() => onGrab(m)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WantedRow({
  movie,
  state,
  onGrab,
}: {
  movie: Movie;
  state: RowState;
  onGrab: () => void;
}) {
  const dim = state.kind === "grabbed";
  return (
    <tr
      className={cn(
        "group transition hover:bg-surface-2/40",
        dim && "opacity-60",
      )}
    >
      <td className="px-3 py-2">
        {movie.poster_url ? (
          <img
            src={movie.poster_url}
            alt=""
            className="aspect-[2/3] w-12 rounded border border-line object-cover"
            loading="lazy"
          />
        ) : (
          <div className="aspect-[2/3] w-12 rounded border border-line bg-surface-2/40" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <Link
          to="/movies/$id"
          params={{ id: String(movie.id) }}
          className="font-display text-base font-medium tracking-display-tight hover:text-accent"
        >
          {movie.title}
        </Link>
        {movie.overview && (
          <div
            className="mt-1 line-clamp-2 max-w-2xl text-xs leading-relaxed text-foreground/55"
            title={movie.overview}
          >
            {movie.overview}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-foreground/65">
        {movie.year ?? "—"}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-foreground/65">
        {movie.runtime_minutes ? `${movie.runtime_minutes}m` : "—"}
      </td>
      <td className="px-3 py-2.5">
        <RowStatus state={state} />
      </td>
      <td className="space-x-2 px-3 py-2 text-right whitespace-nowrap">
        {state.kind === "grabbed" ? (
          <span className="font-mono text-[10px] text-foreground/45">
            {state.record.indexer_name ?? "?"}
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            loading={state.kind === "searching"}
            onClick={onGrab}
          >
            {state.kind === "failed" ? "Retry" : "Search"}
          </Button>
        )}
      </td>
    </tr>
  );
}

function RowStatus({ state }: { state: RowState }) {
  switch (state.kind) {
    case "idle":
      return (
        <Status variant="chip" tone="accent">
          wanted
        </Status>
      );
    case "searching":
      return (
        <Status variant="chip" tone="info" pulse>
          searching
        </Status>
      );
    case "grabbed":
      return (
        <Status variant="chip" tone="good">
          grabbed
        </Status>
      );
    case "failed":
      return (
        <Status variant="chip" tone="bad">
          {state.reason.length > 22 ? "failed" : state.reason}
        </Status>
      );
  }
}
